import React, { useState, useEffect } from 'react';

// ── Konfiguracja ─────────────────────────────────────────────
/** Domyślna długość dłuższego boku (gdy nie podano propsa longSidePx) */
const DEFAULT_LONG_SIDE_PX = 2000;

// ── Typy ─────────────────────────────────────────────────────
type OpeningShape = 'Rectangle' | 'Ellipse';

interface Params {
  widthMm:         number;
  heightMm:        number;
  cornerRadiusMm:  number;
  shape:           OpeningShape;
  openingWidthMm:  number;
  openingHeightMm: number;
}

const DEFAULT: Params = {
  widthMm:         300,
  heightMm:        300,
  cornerRadiusMm:  0,
  shape:           'Ellipse',
  openingWidthMm:  100,
  openingHeightMm: 200,
};

// ── PNG – wstrzyknięcie metadanych DPI (chunk pHYs) ──────────

function readU32BE(a: Uint8Array, i: number): number {
  return ((a[i] << 24) | (a[i+1] << 16) | (a[i+2] << 8) | a[i+3]) >>> 0;
}

function writeU32BE(a: Uint8Array, i: number, v: number): void {
  a[i]   = (v >>> 24) & 0xFF;
  a[i+1] = (v >>> 16) & 0xFF;
  a[i+2] = (v >>>  8) & 0xFF;
  a[i+3] =  v         & 0xFF;
}

/** CRC32 – standardowa implementacja (wielomian 0xEDB88320) */
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[i] = c;
  }
  return t;
})();

function crc32(data: Uint8Array): number {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

/**
 * Wstawia lub zastępuje chunk pHYs w tablicy bajtów PNG.
 * pixelsPerMetre: rozdzielczość w pikselach na metr (jednostka SI).
 */
function setPngDpi(png: Uint8Array, pixelsPerMetre: number): Uint8Array {
  // Przeszukaj chunki PNG, znajdź koniec IHDR i ewentualny istniejący pHYs
  let pos = 8; // pomiń sygnaturę (8 bajtów)
  let ihdrEnd = 33; // domyślnie: 8 (syg.) + 25 (IHDR) = 33
  let pHYsStart = -1, pHYsEnd = -1;

  while (pos + 12 <= png.length) {
    const len      = readU32BE(png, pos);
    const type     = String.fromCharCode(png[pos+4], png[pos+5], png[pos+6], png[pos+7]);
    const chunkEnd = pos + 12 + len;  // 4(len) + 4(type) + len(data) + 4(crc)

    if (type === 'IHDR') ihdrEnd    = chunkEnd;
    if (type === 'pHYs') { pHYsStart = pos; pHYsEnd = chunkEnd; }
    if (type === 'IEND') break;
    pos = chunkEnd;
  }

  // Zbuduj chunk pHYs (9 bajtów danych)
  const data = new Uint8Array(9);
  writeU32BE(data, 0, pixelsPerMetre);  // piksele/jednostkę X
  writeU32BE(data, 4, pixelsPerMetre);  // piksele/jednostkę Y
  data[8] = 1;                          // jednostka = metr

  // CRC liczymy po: "pHYs" (4B) + data (9B)
  const typeBytes = new Uint8Array([0x70, 0x48, 0x59, 0x73]); // 'p','H','Y','s'
  const crcInput  = new Uint8Array(13);
  crcInput.set(typeBytes, 0);
  crcInput.set(data, 4);
  const checksum = crc32(crcInput);

  // Pełny chunk: 4(len) + 4(type) + 9(data) + 4(crc) = 21 bajtów
  const chunk = new Uint8Array(21);
  writeU32BE(chunk, 0, 9);         // długość danych
  chunk.set(typeBytes, 4);         // typ "pHYs"
  chunk.set(data, 8);              // dane
  writeU32BE(chunk, 17, checksum); // CRC

  // Wstaw po IHDR lub zastąp istniejący pHYs
  const insertAt  = pHYsStart !== -1 ? pHYsStart : ihdrEnd;
  const removeEnd = pHYsStart !== -1 ? pHYsEnd   : ihdrEnd;

  const result = new Uint8Array(png.length - (removeEnd - insertAt) + chunk.length);
  result.set(png.subarray(0, insertAt));
  result.set(chunk, insertAt);
  result.set(png.subarray(removeEnd), insertAt + chunk.length);
  return result;
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64.replace(/\s/g, ''));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ── Rysowanie szablonu ────────────────────────────────────────

function drawTemplate(
  ctx: CanvasRenderingContext2D,
  p: Params,
  canvasW: number,
  canvasH: number,
): void {
  // Przelicznik: 1 mm → piksele na tym canvasie
  const pxPerMm = canvasW / p.widthMm;

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, canvasW, canvasH);

  const opW = p.openingWidthMm  * pxPerMm;
  const opH = p.openingHeightMm * pxPerMm;
  const cx  = canvasW / 2;
  const cy  = canvasH / 2;

  ctx.fillStyle = '#ffffff';

  if (p.shape === 'Ellipse') {
    ctx.beginPath();
    ctx.ellipse(cx, cy, opW / 2, opH / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    const x = cx - opW / 2;
    const y = cy - opH / 2;
    const r = p.cornerRadiusMm * pxPerMm;
    if (r <= 0) {
      ctx.fillRect(x, y, opW, opH);
    } else {
      ctx.beginPath();
      (ctx as any).roundRect(x, y, opW, opH, r); // dostępne w Chrome 99+, Firefox 112+
      ctx.fill();
    }
  }
}

// ── Obliczenia rozdzielczości ─────────────────────────────────

function calcResolution(p: Params, longSide: number = DEFAULT_LONG_SIDE_PX) {
  const longerMm = Math.max(p.widthMm, p.heightMm);
  const pxW = p.widthMm  >= p.heightMm
    ? longSide
    : Math.round(longSide * p.widthMm  / p.heightMm);
  const pxH = p.heightMm >= p.widthMm
    ? longSide
    : Math.round(longSide * p.heightMm / p.widthMm);

  const pixelsPerMetre = Math.round(longSide * 1000 / longerMm);
  const dpi            = Math.round(longSide * 25.4  / longerMm);

  return { pxW, pxH, pixelsPerMetre, dpi };
}

/** Generuje szablon na offscreen canvas i zwraca data URL oraz wymiary. */
function renderTemplate(p: Params, longSidePx: number) {
  const { pxW: w, pxH: h, pixelsPerMetre, dpi } = calcResolution(p, longSidePx);
  const off = document.createElement('canvas');
  off.width = w; off.height = h;
  drawTemplate(off.getContext('2d')!, p, w, h);
  return { url: off.toDataURL('image/png'), pxW: w, pxH: h, pixelsPerMetre, dpi };
}

// ── Pole liczbowe (poza komponentem – stały typ, autorepeat działa) ──────────

interface NumFieldProps {
  label:    string;
  value:    number;
  min?:     number;
  step?:    number;
  onChange: (v: number) => void;
}

const NumField: React.FC<NumFieldProps> = ({ label, value, min = 0, step = 1, onChange }) => (
  <div className="tpl-field">
    <label className="tpl-label">{label}</label>
    <input
      type="number"
      className="tpl-input"
      value={value}
      min={min}
      step={step}
      onChange={e => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v) && v >= min) onChange(v);
      }}
    />
  </div>
);

// ── Komponent ────────────────────────────────────────────────

interface Props {
  longSidePx?:              number;   // z ustawień Picture Quality (domyślnie 2000)
  currentTemplateDataUrl?:  string | null;
  currentTemplateWidthMm?:  number;
  currentTemplateHeightMm?: number;
  onUseAsTemplate:          (dataUrl: string, widthMm: number, heightMm: number) => void;
  onRunNormalizer:          (dataUrl: string, widthMm: number, heightMm: number) => void;
  onClose:                  () => void;
}

interface Preview {
  url:      string;
  widthMm:  number;
  heightMm: number;
  pxW:      number;
  pxH:      number;
  dpi:      number;
}

const TemplateDesigner: React.FC<Props> = ({
  longSidePx = DEFAULT_LONG_SIDE_PX,
  currentTemplateDataUrl,
  currentTemplateWidthMm,
  currentTemplateHeightMm,
  onUseAsTemplate,
  onRunNormalizer,
  onClose,
}) => {
  const [p, setP]           = useState<Params>(DEFAULT);
  const [preview, setPreview] = useState<Preview | null>(null);

  const setField = <K extends keyof Params>(key: K, val: Params[K]) =>
    setP(prev => ({ ...prev, [key]: val }));

  const onFieldChange = (key: keyof Params, min: number) => (v: number) => {
    if (v >= min) setField(key, v as any);
  };

  // Przy otwarciu: pokaż aktualny szablon aplikacji (lub wygeneruj domyślny)
  useEffect(() => {
    if (currentTemplateDataUrl) {
      const img = new Image();
      img.onload = () => {
        const pxW = img.naturalWidth;
        const pxH = img.naturalHeight;
        const wMm = currentTemplateWidthMm  ?? DEFAULT.widthMm;
        const hMm = currentTemplateHeightMm ?? DEFAULT.heightMm;
        const longSidePxLocal = Math.max(pxW, pxH);
        const dpi = longSidePxLocal > 0
          ? Math.round(longSidePxLocal * 25.4 / Math.max(wMm, hMm))
          : 0;
        setPreview({ url: currentTemplateDataUrl, widthMm: wMm, heightMm: hMm, pxW, pxH, dpi });
      };
      img.src = currentTemplateDataUrl;
    } else {
      const { url, pxW, pxH, dpi } = renderTemplate(DEFAULT, longSidePx);
      setPreview({ url, pxW, pxH, dpi, widthMm: DEFAULT.widthMm, heightMm: DEFAULT.heightMm });
    }
  }, []); // eslint-disable-line

  // ── Generowanie szablonu z parametrów ─────────────────────
  const handleGenerate = () => {
    const { url, pxW, pxH, dpi } = renderTemplate(p, longSidePx);
    setPreview({ url, pxW, pxH, dpi, widthMm: p.widthMm, heightMm: p.heightMm });
  };

  // ── Zapis PNG ─────────────────────────────────────────────
  const handleSavePng = () => {
    const { url, pixelsPerMetre } = renderTemplate(p, longSidePx);
    const pngBytes = base64ToBytes(url.split(',')[1]);
    const withDpi  = setPngDpi(pngBytes, pixelsPerMetre);

    const blob = new Blob([withDpi], { type: 'image/png' });
    const href = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.download = 'template.png';
    a.href     = href;
    a.click();
    URL.revokeObjectURL(href);
  };

  // ── Uruchomienie normalizatora ────────────────────────────
  const handleRunNormalizer = () => {
    if (!preview) return;
    onRunNormalizer(preview.url, preview.widthMm, preview.heightMm);
  };

  return (
    <div className="layers-overlay">
      <div className="tpl-dialog">

        {/* ── Lewa kolumna ── */}
        <div className="tpl-left">
          <NumField label="Width (mm):"          value={p.widthMm}         min={1}        onChange={onFieldChange('widthMm', 1)} />
          <NumField label="Height (mm):"         value={p.heightMm}        min={1}        onChange={onFieldChange('heightMm', 1)} />
          <NumField label="Corner Radius (mm):"  value={p.cornerRadiusMm}  step={0.5}     onChange={onFieldChange('cornerRadiusMm', 0)} />

          <div className="tpl-field">
            <label className="tpl-label">Opening Shape:</label>
            <select className="tpl-select" value={p.shape}
              onChange={e => setField('shape', e.target.value as OpeningShape)}>
              <option value="Rectangle">Rectangle</option>
              <option value="Ellipse">Ellipse</option>
            </select>
          </div>

          <NumField label="Opening Width (mm):"  value={p.openingWidthMm}  min={1} step={0.5} onChange={onFieldChange('openingWidthMm', 1)} />
          <NumField label="Opening Height (mm):" value={p.openingHeightMm} min={1} step={0.5} onChange={onFieldChange('openingHeightMm', 1)} />

          <div className="tpl-spacer" />

          <button onClick={handleGenerate}>Generate</button>
          <button onClick={handleSavePng}>Save PNG</button>

          <div className="tpl-gap" />
          <button onClick={handleRunNormalizer} disabled={!preview}>Run Normalizer</button>
          <div className="tpl-gap" />
          <button
            disabled={!preview}
            onClick={() => preview && onUseAsTemplate(preview.url, preview.widthMm, preview.heightMm)}
          >
            Use as templ and close
          </button>
          <div className="tpl-gap" />
          <button onClick={onClose}>Close</button>
        </div>

        {/* ── Prawa kolumna: podgląd ── */}
        <div className="tpl-right">
          {preview
            ? <img src={preview.url} alt="template preview" className="tpl-canvas" />
            : <div className="tpl-canvas" style={{ width: 200, height: 200, background: '#222' }} />
          }
          <div className="tpl-info">
            {preview ? (
              <>
                {preview.pxW}&thinsp;×&thinsp;{preview.pxH}&thinsp;px
                &emsp;·&emsp;
                {preview.widthMm}&thinsp;×&thinsp;{preview.heightMm}&thinsp;mm
                {preview.dpi > 0 && <>&emsp;·&emsp;{preview.dpi}&thinsp;DPI</>}
              </>
            ) : 'Loading…'}
          </div>
        </div>

      </div>
    </div>
  );
};

export default TemplateDesigner;
